"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.syncRequestSchema = exports.logEventSchema = exports.vitalsEventSchema = exports.extraSubstanceEventSchema = exports.symptomEventSchema = exports.doseEventSchema = void 0;
/**
 * The wire contract for logging.
 *
 * One schema, imported by both the PWA and the Lambdas, so the client cannot send a
 * shape the server does not expect. Parsing happens at the edge; everything inside
 * works with typed values.
 */
const zod_1 = require("zod");
const domain_js_1 = require("./domain.js");
/** UUIDv7, generated on the device at the moment of the tap. */
const eventIdSchema = zod_1.z
    .string()
    .uuid()
    .describe('Client-generated UUIDv7; the idempotency key for the whole system');
const isoInstant = zod_1.z.string().datetime({ offset: true });
const symptomSchema = zod_1.z.enum([...domain_js_1.SYMPTOMS, ...domain_js_1.RED_FLAG_SYMPTOMS]);
/**
 * What every logged event carries regardless of kind.
 *
 * `clientCapturedAt` is the truth of record: it is what the person saw on the clock
 * when they tapped. The server never substitutes its own receive time — a dose taken
 * in a basement at 07:12 and synced at 12:40 is a 07:12 dose.
 */
const baseEventSchema = zod_1.z.object({
    eventId: eventIdSchema,
    clientCapturedAt: isoInstant,
    clientTz: zod_1.z.string().min(1),
    clientTzOffsetMinutes: zod_1.z.number().int(),
    /** Measured against our own Date response header; grades how much to trust the clock. */
    clockSkewMsAtCapture: zod_1.z.number().int().nullable().optional(),
    capturedOffline: zod_1.z.boolean().optional(),
    timeSource: zod_1.z.enum(domain_js_1.TIME_SOURCES).default('NOW'),
    notes: zod_1.z.string().max(2000).optional(),
});
exports.doseEventSchema = baseEventSchema.extend({
    eventType: zod_1.z.literal('DOSE_TAKEN'),
    medId: zod_1.z.string().min(1),
    medVersion: zod_1.z.number().int().positive(),
    quantity: zod_1.z.number().positive().default(1),
    /** Absent for an as-needed dose, which belongs to no scheduled slot. */
    slotId: zod_1.z.string().min(1).nullable().optional(),
    /** True when the person was warned about a possible extra dose and continued. */
    overdoseWarningShown: zod_1.z.boolean().optional(),
    warningAcknowledged: zod_1.z.boolean().optional(),
    prnReason: zod_1.z.string().max(200).optional(),
});
exports.symptomEventSchema = baseEventSchema.extend({
    eventType: zod_1.z.literal('SYMPTOM'),
    symptoms: zod_1.z.array(symptomSchema).min(1),
    severity: zod_1.z.enum(domain_js_1.SEVERITIES).optional(),
    onset: zod_1.z.enum(domain_js_1.ONSETS).default('NOW'),
    durationMinutes: zod_1.z.number().int().nonnegative().optional(),
});
exports.extraSubstanceEventSchema = baseEventSchema.extend({
    eventType: zod_1.z.literal('EXTRA_SUBSTANCE'),
    substanceKey: zod_1.z.enum(domain_js_1.SUBSTANCE_KEYS),
    substanceName: zod_1.z.string().max(120).optional(),
    strength: zod_1.z.string().max(60).optional(),
    quantity: zod_1.z.number().positive().optional(),
    reason: zod_1.z.enum(domain_js_1.SUBSTANCE_REASONS).optional(),
});
exports.vitalsEventSchema = baseEventSchema.extend({
    eventType: zod_1.z.literal('VITALS'),
    pulseBpm: zod_1.z.number().int().min(20).max(250).optional(),
    systolic: zod_1.z.number().int().min(50).max(260).optional(),
    diastolic: zod_1.z.number().int().min(20).max(200).optional(),
    weightLb: zod_1.z.number().min(20).max(1000).optional(),
    temperatureF: zod_1.z.number().min(80).max(115).optional(),
});
exports.logEventSchema = zod_1.z.discriminatedUnion('eventType', [
    exports.doseEventSchema,
    exports.symptomEventSchema,
    exports.extraSubstanceEventSchema,
    exports.vitalsEventSchema,
]);
/** Outbox drain. Capped so one request cannot be unbounded work. */
exports.syncRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().min(1),
    events: zod_1.z.array(exports.logEventSchema).min(1).max(50),
});
//# sourceMappingURL=events.js.map